import React from "react";
import {
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";

/**
 * Props for the AnnotationColorSelector component
 */
interface AnnotationColorSelectorProps {
  /** The currently selected color value (hex string) */
  selectedColor: string;
  /** Callback function triggered when a color is selected */
  onSelectColor: (event: SelectChangeEvent<string>) => void;
  /** Whether to show the input label above the selector */
  showLabel?: boolean;
  /** Whether to show color names alongside color swatches */
  showNames?: boolean;
  /** Predefined color options to display in the selector */
  colorOptions: string[];
  /** Name attribute for the form control */
  name?: string;
  /** Minimum width of the selector in pixels */
  minWidth?: number;
  /** Custom styles to apply to the Select component */
  selectSX?: SxProps<Theme>;
  /** Additional props to pass to the Select's MenuProps */
  menuProps?: any;
}



/**
 * Utility function to validate and find a color in the color options
 * @param color - The color hex string to search for
 * @param colorOptions - Array of available color options
 * @returns The validated color if found, otherwise returns the first color in the options
 */
export const findColor = (color: string, colorOptions: string[]): string => {
  // Check if the provided color exists in our options
  if (colorOptions.includes(color)) {
    return color;
  }
  // Fallback to the first available color if not found
  return colorOptions[0] || "#000000";
};

/**
 * Main component for selecting annotation colors in the model viewer
 * Provides a dropdown with color options for marking up 3D models
 */
const AnnotationColorSelector: React.FC<AnnotationColorSelectorProps> = ({
  selectedColor,
  onSelectColor,
  showLabel = true,
  showNames = true,
  colorOptions,
  name = "color",
  minWidth = 160,
  selectSX,
  menuProps,
}) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        backgroundColor: theme.palette.grey[300],
      }}
    >
      <FormControl
        fullWidth={showLabel}
        aria-labelledby={!showLabel ? undefined : `${name}-select-label`}
      >
        {/* Conditional label display */}
        {showLabel && (
          <InputLabel id={`${name}-select-label`} color="primary">
            Color
          </InputLabel>
        )}

        <Select
          fullWidth={showLabel}
          name={name}
          label={!showLabel ? undefined : name}
          id={`${name}-select`}
          aria-labelledby={`${name}-select-label`}
          labelId={`${name}-select-label`}
          value={selectedColor}
          onChange={onSelectColor}
          renderValue={(value) => (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{ width: '100%' }}
            >
              <div
                style={{
                  backgroundColor: value,
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  flexShrink: 0,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
                aria-label={`${value} color selected`}
              />
            </Stack>
          )}
          MenuProps={{
            ...menuProps,
          }}
          sx={{
            minWidth: showNames ? `${minWidth}px` : undefined,
            height: "43.42px",
            ...selectSX,
          }}
        >
          {colorOptions.map((hexColor) => {
            return (
              <MenuItem key={hexColor} value={hexColor} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: '100%' }}
                >
                  <div
                    key={hexColor}
                    style={{
                      backgroundColor: hexColor,
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      flexShrink: 0,
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                    aria-label={`${hexColor} color`}
                  />
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Card>
  );
};

export default AnnotationColorSelector;
